import { LocalizedText } from "@/i18n/LocalizedText";
import { useLocale } from "@/i18n/LocaleProvider";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Spinner } from "./ui/spinner";
import { Button } from "./ui/button";
import noData from "../assets/no-data.svg";
import { flexRender } from "@tanstack/react-table";
import type { Table as TableType } from "@tanstack/react-table";
import { Progress } from "./ui/progress";
import { Skeleton } from "./ui/skeleton";
import type { Limits } from "@/store/useDataStore";
import PaginatedControls from "./Pagination";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { useNavigate } from "react-router";
import { normalizeUsageMetric } from "@/utils/usage";

type PromptsTableProps<TData> = {
  table: TableType<TData>;
  limitPrompts?: Limits | null;
  tableAction?: () => void;
  isLoading: boolean;
  isFiltered?: boolean;
  onOpenPromptDetails?: (prompt: TData) => void;
  highlightTargetPromptId?: string | null;
};

function PromptsTable<TData>({
  table,
  limitPrompts,
  isFiltered = false,
  tableAction,
  isLoading,
  onOpenPromptDetails,
  highlightTargetPromptId,
}: PromptsTableProps<TData>) {
  const { t } = useLocale();
  const navigate = useNavigate();
  const columnCount = table.getVisibleLeafColumns().length;
  const promptUsage = normalizeUsageMetric(limitPrompts?.prompts);

  return (
    <>
      <div className="overflow-hidden rounded-md">
        <Table>
          <TableHeader className="bg-[#E5E5E5]">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columnCount}
                  className="h-24 text-center relative"
                >
                  <div className="relative flex flex-col gap-2">
                    <div className="z-10 gap-1 flex-col absolute flex justify-center items-center w-full h-full">
                      <div className="flex gap-2 items-center flex-col p-2 bg-muted rounded-md">
                        <Spinner
                          className="text-[#FF5E00]"
                          variant="circle"
                        ></Spinner>
                      </div>
                      <div>
                        <LocalizedText id="legacy.processing.3jbdvy" />
                      </div>
                    </div>
                    {Array.from({ length: 6 }).map((_, index) => {
                      return (
                        <div key={index} className="flex w-full relative">
                          <div className="flex items-center gap-2 w-full">
                            <Skeleton className="h-[20px] w-[25px] rounded-full" />
                            <div className="space-y-2 w-full flex gap-2 ">
                              <Skeleton className="h-4 w-[75%]" />
                              <Skeleton className="h-4 w-[25%]" />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row: any) => (
                <TableRow
                  key={row.original?.id || row.id}
                  className={`relative ${highlightTargetPromptId && "bg-white hover:bg-white"}`}
                  data-state={row.getIsSelected() && "selected"}
                  data-onboarding-target={
                    highlightTargetPromptId &&
                    String(row.original?.id || "") ===
                      String(highlightTargetPromptId)
                      ? `prompt-row-${highlightTargetPromptId}`
                      : undefined
                  }
                  onClick={() => onOpenPromptDetails?.(row.original as TData)}
                >
                  {row.getVisibleCells().map((cell: any) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columnCount}
                  className="h-24 text-center relative"
                >
                  <div className="flex flex-col gap-2 items-center">
                    <div>
                      <img src={noData}></img>
                    </div>
                    <div className="text-lg text-foreground">
                      {isFiltered
                        ? t("common.allPromptsFiltered")
                        : t("common.noPromptsYet")}
                    </div>
                    <div className="text-sm text-muted-foreground max-w-[288px] whitespace-break-spaces">
                      {isFiltered
                        ? t("common.clearSearchToSeePrompts")
                        : t("common.noPromptsCreated")}
                    </div>
                    {tableAction && !isFiltered && (
                      <Button
                        variant="outline"
                        className="hover:bg-[#ff8138] w-fit hover:text-white text-white cursor-pointer bg-[#FF5E00]"
                        onClick={tableAction}
                      >
                        <LocalizedText id="legacy.add.prompt.1vpa268" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between space-x-2 py-4">
        <div className="flex items-center gap-2">
          <div className="text-muted-foreground text-sm ">
            {promptUsage.isAvailable
              ? `${promptUsage.used}/${promptUsage.limit}`
              : "—/—"}{" "}
            {t("common.prompts")}
          </div>
          {promptUsage.isLimitReached ? (
            <Tooltip>
              <TooltipTrigger className="has-[>svg]:px-2" asChild>
                <Progress
                  className=" cursor-pointer bg-[#ffcdb1] relative h-2 overflow-hidden rounded-full w-[80px]"
                  value={promptUsage.progress}
                />
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className="flex max-w-[328px] gap-3 p-3"
              >
                <div className="text-sx text-[#FAFAFA] border-[#404040] border-r ">
                  <LocalizedText id="legacy.you.have.reached.prompts.limit.upgrade.your.plan.11l1otg" />
                </div>
                <Button
                  className="hover:bg-[#ff8138] hover:text-white text-white cursor-pointer bg-[#FF5E00]"
                  onClick={() => {
                    navigate(`/dashboard/plans-pricing`);
                  }}
                >
                  <LocalizedText id="legacy.see.plans.pifaie" />
                </Button>
              </TooltipContent>
            </Tooltip>
          ) : (
            <Progress
              className="bg-[#ffcdb1] relative h-2 overflow-hidden rounded-full w-[80px]"
              value={promptUsage.progress}
            />
          )}
        </div>
        <div className="flex space-x-1">
          <PaginatedControls table={table} />
        </div>
      </div>
    </>
  );
}

export default PromptsTable;
